import { ToastContext } from "context/ToastContext";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Message } from "primereact/message";
import { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useUploadFileMutation } from "store/queries/chat";
import styled from "styled-components";
import { IUploadFileRequest } from "types/Chat";

const FormContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;
`;

const FormField = styled.div`
  margin-bottom: 16px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 24px;
`;

const HintText = styled.div`
  margin-top: 8px;
  padding: 12px;
  background-color: #f0f8ff;
  border: 1px solid #cce7ff;
  border-radius: 4px;
  font-size: 14px;
  color: #2c5282;
`;

const fileIdentifierOptions = [
  {
    label: "Eligibility Business Rules Documentation",
    value: "EligibilityProcessingGuide",
  },
  {
    label: "Pricing Adjustment Business Rules Documentation",
    value: "PricingAdjustmentsGuide",
  },
  {
    label: "Contract Management Business Rules Documentation",
    value: "ContractManagementGuide",
  },
  { label: "Custom", value: "Custom" },
];

export function UploadFilePage() {
  const navigate = useNavigate();
  const { current: toastElement } = useContext(ToastContext);
  const [uploadFile, { isLoading }] = useUploadFileMutation();

  const [formData, setFormData] = useState({
    fileIdentifier: "",
    name: "",
    content: "",
  });

  const showHintText =
    formData.fileIdentifier === "EligibilityProcessingGuide" ||
    formData.fileIdentifier === "PricingAdjustmentsGuide" ||
    formData.fileIdentifier === "ContractManagementGuide";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const submitData: IUploadFileRequest = {
        fileIdentifier: formData.fileIdentifier,
        name: formData.name,
      };

      // Add content if it's a custom file
      if (formData.fileIdentifier === "Custom" && formData.content) {
        (submitData as any).content = formData.content;
      }

      await uploadFile(submitData).unwrap();

      toastElement?.show({
        severity: "success",
        summary: "Success!",
        detail: "File uploaded successfully.",
      });

      navigate("/chatbot/files");
    } catch (error: any) {
      toastElement?.show({
        severity: "error",
        summary: "Error",
        detail: error?.data?.message || "Failed to upload file.",
      });
    }
  };

  const handleCancel = () => {
    navigate("/chatbot/files");
  };

  return (
    <FormContainer>
      <Card title="Upload New File">
        <form onSubmit={handleSubmit}>
          <FormField>
            <Label htmlFor="fileIdentifier">File Identifier *</Label>
            <Dropdown
              id="fileIdentifier"
              value={formData.fileIdentifier}
              onChange={(e) =>
                setFormData({ ...formData, fileIdentifier: e.value })
              }
              options={fileIdentifierOptions}
              placeholder="Select file identifier"
              className="w-full"
              required
            />
            {showHintText && (
              <HintText>
                <strong>Note:</strong> Guide files are managed as source code
                within our application codebase to ensure they stay synchronized
                with business logic changes. When you upload these file types,
                the system automatically reads the current version from the
                application and uploads it to OpenAI&apos;s servers. This
                approach ensures all chat models reference the same, up-to-date
                business rules while maintaining version control through our
                development workflow. The content field is hidden because these
                files must be updated in the codebase.
              </HintText>
            )}
          </FormField>

          <FormField>
            <Label htmlFor="name">File Name *</Label>
            <InputText
              id="name"
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              required
              className="w-full"
              placeholder="Enter file name"
            />
          </FormField>

          {formData.fileIdentifier === "Custom" && (
            <FormField>
              <Label htmlFor="content">File Content *</Label>
              <InputTextarea
                id="content"
                value={formData.content}
                onChange={(e) =>
                  setFormData({ ...formData, content: e.target.value })
                }
                required={formData.fileIdentifier === "Custom"}
                rows={10}
                className="w-full"
                placeholder="Enter file content"
              />
            </FormField>
          )}

          <ButtonContainer>
            <Button
              type="button"
              label="Cancel"
              className="p-button-outlined"
              onClick={handleCancel}
            />
            <Button
              type="submit"
              label="Upload File"
              className="p-button-raised"
              loading={isLoading}
              disabled={
                !formData.fileIdentifier ||
                !formData.name ||
                (formData.fileIdentifier === "Custom" && !formData.content)
              }
            />
          </ButtonContainer>
        </form>
      </Card>
    </FormContainer>
  );
}
